import AuthService from "../../../api/services/AuthService";

export function parseMarginLevel(ml) {
    if (ml == null || ml === "") return null;
    const n = parseFloat(ml);
    return Number.isFinite(n) ? n : null;
}

export function formatMarginLevel(ml) {
    if (ml == null || !Number.isFinite(ml)) return "—";
    if (ml >= 999) return "∞";
    return ml.toFixed(2);
}

export function formatThresholdLevel(n) {
    if (n == null || !Number.isFinite(n)) return "—";
    return parseFloat(n.toFixed(4)).toString();
}

export function parseMarginThresholds(source = {}) {
    const risk = source.risk && typeof source.risk === "object" ? source.risk : {};
    const marginCallLevel = parseMarginLevel(
        source.margin_call_level
        ?? source.marginCallLevel
        ?? risk.margin_call_level
        ?? source.margin_call_margin_level
    );
    const liquidationMarginLevel = parseMarginLevel(
        source.liquidation_margin_level
        ?? source.liquidationMarginLevel
        ?? risk.liquidation_margin_level
        ?? source.liquidation_level
    );
    return { marginCallLevel, liquidationMarginLevel };
}

export function computeInitialMarginLevel(leverage) {
    const L = parseFloat(leverage);
    if (!Number.isFinite(L) || L <= 1) return null;
    return L / (L - 1);
}

export function parseEffectiveMultiple(source = {}) {
    return parseMarginLevel(
        source.effective_multiple
        ?? source.effective_leverage
        ?? source.leverage
        ?? source.default_leverage
        ?? source.max_leverage
    );
}

/** Resolve thresholds from API fields, including rate-based fallbacks on account payloads. */
export function resolveMarginThresholds(source = {}) {
    const parsed = parseMarginThresholds(source);
    let { marginCallLevel, liquidationMarginLevel } = parsed;

    if (marginCallLevel == null) {
        const warnRate = parseFloat(source.warning_margin_rate ?? source.margin_call_rate);
        if (Number.isFinite(warnRate)) marginCallLevel = 1 + warnRate;
    }
    if (liquidationMarginLevel == null) {
        const maintRate = parseFloat(source.maintenance_margin_rate ?? source.liquidation_margin_rate);
        if (Number.isFinite(maintRate)) liquidationMarginLevel = 1 + maintRate;
    }

    return { marginCallLevel, liquidationMarginLevel };
}

export function normalizeMarginPairKey(pair, base, quote) {
    if (base && quote) return `${base}${quote}`.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return String(pair || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

export function buildMarginAccountLookup(accounts = []) {
    const map = new Map();
    for (const acc of accounts) {
        const compact = normalizeMarginPairKey(acc.pair, acc.base_asset, acc.quote_asset);
        if (compact) map.set(compact, acc);
        if (acc.pair_id) map.set(String(acc.pair_id), acc);
        if (acc.pair) map.set(String(acc.pair).toUpperCase(), acc);
    }
    return map;
}

/** Fetch per-pair account detail so margin call / liquidation thresholds are available. */
export async function enrichMarginAccounts(accounts = []) {
    const list = Array.isArray(accounts) ? accounts : [];
    const enriched = await Promise.all(list.map(async (acc) => {
        const pairId = acc?.pair_id || acc?._id;
        if (!pairId) return acc;
        try {
            const res = await AuthService.marginAccount(pairId);
            if (res?.success && res.data) return { ...acc, ...res.data };
        } catch {
            /* keep list payload */
        }
        return acc;
    }));
    return enriched;
}

/** Merge fresh list snapshots into enriched accounts without dropping threshold fields. */
export function mergeMarginAccountSnapshots(existing = [], fresh = []) {
    const lookup = buildMarginAccountLookup(existing);
    const freshList = Array.isArray(fresh) ? fresh : [];
    const seen = new Set();

    const merged = freshList.map((acc) => {
        const pairKey = normalizeMarginPairKey(acc.pair, acc.base_asset, acc.quote_asset);
        const prior =
            (pairKey && lookup.get(pairKey))
            || (acc.pair_id && lookup.get(String(acc.pair_id)))
            || {};
        const id = String(acc.pair_id || pairKey || acc.pair || "");
        if (id) seen.add(id);
        return { ...prior, ...acc };
    });

    for (const acc of existing) {
        const id = String(acc.pair_id || normalizeMarginPairKey(acc.pair, acc.base_asset, acc.quote_asset) || "");
        if (id && !seen.has(id)) merged.push(acc);
    }

    return merged;
}

/** Merge a position row with its isolated margin account (thresholds, pair_id, leverage). */
export function resolveMarginPositionContext(pos, lookup, pairIdFallback = "") {
    const pairKey = normalizeMarginPairKey(pos?.pair);
    const acc =
        (pairKey && lookup?.get?.(pairKey))
        || (pos?.pair_id && lookup?.get?.(String(pos.pair_id)))
        || (pairIdFallback && lookup?.get?.(String(pairIdFallback)))
        || null;

    const accountMl = parseMarginLevel(acc?.margin_level);
    const positionMl = parseMarginLevel(pos?.margin_level);

    return {
        ...(acc || {}),
        ...pos,
        pair_id: pos?.pair_id || acc?.pair_id || acc?._id || pairIdFallback || "",
        pair: pos?.pair || acc?.pair,
        // Account margin level is authoritative for isolated pair risk coloring.
        margin_level: accountMl != null ? acc.margin_level : pos?.margin_level,
        margin_call_level: acc?.margin_call_level ?? pos?.margin_call_level,
        liquidation_margin_level: acc?.liquidation_margin_level ?? pos?.liquidation_margin_level,
        warning_margin_rate: acc?.warning_margin_rate ?? pos?.warning_margin_rate,
        maintenance_margin_rate: acc?.maintenance_margin_rate ?? pos?.maintenance_margin_rate,
        leverage: pos?.leverage ?? acc?.leverage ?? acc?.default_leverage,
        effective_multiple: pos?.effective_multiple ?? acc?.effective_multiple ?? acc?.effective_leverage,
        balances: pos?.balances ?? acc?.balances,
        base_borrowed: pos?.base_borrowed ?? acc?.base_borrowed,
        quote_borrowed: pos?.quote_borrowed ?? acc?.quote_borrowed,
        _positionMarginLevel: positionMl,
    };
}

export function pairHasDebt(source = {}, ml = null) {
    const baseBorrowed = parseFloat(source.base_borrowed ?? source.borrowedBase ?? 0);
    const quoteBorrowed = parseFloat(source.quote_borrowed ?? source.borrowedQuote ?? 0);
    if (baseBorrowed > 0 || quoteBorrowed > 0) return true;
    const balances = source.balances;
    if (balances) {
        const bb = parseFloat(balances.base_borrowed ?? 0);
        const qb = parseFloat(balances.quote_borrowed ?? 0);
        if (bb > 0 || qb > 0) return true;
    }
    const level = ml != null ? ml : parseMarginLevel(source.margin_level ?? source.marginLevel);
    return level != null && Number.isFinite(level) && level > 0 && level < 999;
}

export function buildMarginRiskRow(source = {}, pairIdFallback = "") {
    const thresholds = resolveMarginThresholds(source);
    const pairRaw = source.pair ?? source.pairRaw ?? "";
    return {
        ...source,
        pair_id: source.pair_id || pairIdFallback || "",
        pair: pairRaw || source.pair,
        pairRaw: pairRaw || source.pair,
        margin_call_level: source.margin_call_level ?? thresholds.marginCallLevel,
        liquidation_margin_level: source.liquidation_margin_level ?? thresholds.liquidationMarginLevel,
        hasDebt: pairHasDebt(source),
    };
}

export function getMarginLevelStatus(ml, thresholds = {}, { hasDebt = true } = {}) {
    const { marginCallLevel, liquidationMarginLevel } = resolveMarginThresholds(thresholds);

    if (!hasDebt) {
        return {
            key: "safe",
            label: "Safe",
            color: "#16a34a",
            description: "No outstanding debt on this isolated account. Margin level is not applicable.",
        };
    }

    if (marginCallLevel == null || liquidationMarginLevel == null) {
        return {
            key: "unavailable",
            label: "Unavailable",
            color: "#9ca3af",
            description: "Margin risk thresholds are not available for this account.",
        };
    }

    if (ml == null || !Number.isFinite(ml)) {
        return {
            key: "unavailable",
            label: "Unavailable",
            color: "#9ca3af",
            description: "Margin level data is not available for this position.",
        };
    }

    if (ml > marginCallLevel) {
        return {
            key: "normal",
            label: "Normal",
            color: "#16a34a",
            description: "Account is healthy. Borrow and transfer-out are allowed, as long as ML stays above the margin call level after the action.",
        };
    }

    if (ml > liquidationMarginLevel) {
        return {
            key: "margin_call",
            label: "Margin call",
            color: "#f59e0b",
            description: "Add collateral (transfer in) or repay debt to avoid liquidation. New borrow and transfer-out are blocked. Trading is still allowed.",
        };
    }

    return {
        key: "liquidated",
        label: "Liquidated",
        color: "#dc2626",
        description: "Liquidation is triggered. All open orders on this pair are cancelled. The position is closed at market. Interest and debt are repaid. A 2% liquidation fee is charged. Leftover assets stay in this isolated account.",
    };
}
